import { Fragment, useEffect, useMemo, useState } from 'react'
import { fxPurchaseOrdersApi, fxSuppliersApi, fxProductsApi, fxFuxionCatalogApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

type PurchaseOrderStatus = 'en_transito' | 'recibido' | 'cancelado'

interface Supplier {
  id: number
  name: string
  supplier_type: 'producto' | 'servicio'
  is_active: boolean
}

interface Product {
  id: number
  name: string
  fuxion_catalog_id: number | null
  is_active: boolean
}

interface FuxionCatalogItem {
  id: number
  product_name: string
  presentation: string
  list_price: number
  price_20: number
  price_25: number
  price_30: number
  price_40: number
  price_50: number
}

interface PurchaseOrderItem {
  id: number
  purchase_order_id: number
  product_id: number
  product_name: string
  quantity: number
  list_price: number | null
  unit_cost: number
}

interface PurchaseOrder {
  id: number
  supplier_id: number
  supplier_name: string
  discount_percentage: number | null
  order_date: string
  expected_arrival_date: string | null
  arrival_date: string | null
  status: PurchaseOrderStatus
  notes: string | null
  created_at: string
  items: PurchaseOrderItem[]
}

const DISCOUNT_TIERS = [20, 25, 30, 40, 50] as const

const STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  en_transito: 'En tránsito',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
}
const STATUS_BADGE_CLASSES: Record<PurchaseOrderStatus, string> = {
  en_transito: 'bg-yellow-100 text-yellow-800',
  recibido: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
}

const todayIsoDate = () => new Date().toISOString().slice(0, 10)

interface OrderHeaderForm {
  supplier_id: string
  order_date: string
  expected_arrival_date: string
  discount_percentage: string // '' | '20' | '25' | '30' | '40' | '50'
  notes: string
}

const emptyOrderHeaderForm = (): OrderHeaderForm => ({
  supplier_id: '', order_date: todayIsoDate(), expected_arrival_date: '', discount_percentage: '', notes: '',
})

interface CartItem {
  product_id: number
  product_name: string
  quantity: number
  unit_cost: number
  list_price: number | null
  // 'catalog' -- cost resolved server-side from the chosen discount tier,
  // no unit_cost sent in the payload. 'manual' -- admin-entered cost, sent as-is.
  source: 'catalog' | 'manual'
}

/** Multi-product purchase orders -- replaces the old one-product-at-a-time
 * "Comprar stock" flow (now frozen history in PurchasesPage). An order
 * starts en_transito (adds to stock_in_transit_qty) and later becomes
 * recibido (moves into sellable stock_qty) or cancelado. This is the
 * "inventory ledger" view: what's in transit vs. already on hand, at what
 * price, from when. Not to be confused with FxOrdersPage ("Solicitudes"),
 * which is the opposite direction -- a customer wanting to buy from us. */
export default function FxPurchaseOrdersPage() {
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [catalog, setCatalog] = useState<FuxionCatalogItem[]>([])
  const [loading, setLoading] = useState(true)

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [headerForm, setHeaderForm] = useState<OrderHeaderForm>(emptyOrderHeaderForm())
  const [cart, setCart] = useState<CartItem[]>([])
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [itemQuantity, setItemQuantity] = useState('1')
  const [manualUnitCost, setManualUnitCost] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [receivingOrder, setReceivingOrder] = useState<PurchaseOrder | null>(null)
  const [arrivalDate, setArrivalDate] = useState('')
  const [actionError, setActionError] = useState('')
  const [actingId, setActingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      const [ordersRes, suppliersRes, productsRes, catalogRes] = await Promise.all([
        fxPurchaseOrdersApi.list(),
        fxSuppliersApi.list(),
        fxProductsApi.list(),
        fxFuxionCatalogApi.list(),
      ])
      setOrders(ordersRes.data.purchase_orders || [])
      // Only "producto" suppliers sell physical stock -- a "servicio" supplier
      // (marketing, logistica, contabilidad) never appears here, matching the
      // same rule the backend enforces when creating a purchase order.
      setSuppliers(
        (suppliersRes.data.suppliers || []).filter((s: Supplier) => s.is_active && s.supplier_type === 'producto')
      )
      setProducts((productsRes.data.products || []).filter((p: Product) => p.is_active))
      setCatalog(catalogRes.data.catalog || [])
    } catch (err) {
      console.error('Error fetching FX purchase orders:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(orders, {
    key: 'order_date',
    direction: 'desc',
  })

  const selectedTier = DISCOUNT_TIERS.find((t) => String(t) === headerForm.discount_percentage)

  const selectedProduct = selectedProductId ? products.find((p) => p.id === selectedProductId) || null : null
  const selectedCatalogItem = selectedProduct?.fuxion_catalog_id
    ? catalog.find((c) => c.id === selectedProduct.fuxion_catalog_id) || null
    : null
  const usesTierForSelected = selectedCatalogItem !== null && selectedTier !== undefined
  const autoUnitCost = usesTierForSelected ? (selectedCatalogItem as any)[`price_${selectedTier}`] as number : null

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unit_cost * item.quantity, 0),
    [cart]
  )

  const orderTotal = (order: PurchaseOrder) =>
    order.items.reduce((sum, item) => sum + item.unit_cost * item.quantity, 0)

  const openNew = () => {
    setEditingId(null)
    setHeaderForm(emptyOrderHeaderForm())
    setCart([])
    setSelectedProductId(null)
    setItemQuantity('1')
    setManualUnitCost('')
    setError('')
    setShowForm(true)
  }

  const openEdit = (order: PurchaseOrder) => {
    setEditingId(order.id)
    setHeaderForm({
      supplier_id: String(order.supplier_id),
      order_date: order.order_date.slice(0, 10),
      expected_arrival_date: order.expected_arrival_date ? order.expected_arrival_date.slice(0, 10) : '',
      discount_percentage: order.discount_percentage ? String(order.discount_percentage) : '',
      notes: order.notes || '',
    })
    setCart(
      order.items.map((item) => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        list_price: item.list_price,
        // A catalog-resolved item always carries a list_price snapshot; a
        // manually-costed item never does -- same signal the create flow
        // uses to decide which cost field to show for a cart line.
        source: item.list_price !== null ? 'catalog' : 'manual',
      }))
    )
    setSelectedProductId(null)
    setItemQuantity('1')
    setManualUnitCost('')
    setError('')
    setShowForm(true)
  }

  const handleHeaderChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setHeaderForm((prev) => ({ ...prev, [name]: value }))
    // Every cart line's cost is resolved against the order's discount tier at
    // the moment it's added -- changing the tier afterward would leave stale
    // costs behind, so clear the cart and have the admin re-add items.
    if (name === 'discount_percentage' && cart.length > 0) {
      setCart([])
      setError('Se vació el carrito porque cambiaste el descuento -- vuelve a agregar los productos.')
    }
  }

  const handleAddToCart = () => {
    if (!selectedProductId || !selectedProduct) {
      setError('Selecciona un producto')
      return
    }
    if (cart.some((item) => item.product_id === selectedProductId)) {
      setError('Este producto ya está en la orden')
      return
    }
    const quantity = parseInt(itemQuantity, 10)
    if (isNaN(quantity) || quantity <= 0) {
      setError('Ingresa una cantidad válida')
      return
    }

    let unitCost: number
    const listPrice: number | null = selectedCatalogItem?.list_price ?? null
    let source: 'catalog' | 'manual'
    if (usesTierForSelected && autoUnitCost !== null) {
      unitCost = autoUnitCost
      source = 'catalog'
    } else {
      const manual = parseFloat(manualUnitCost)
      if (isNaN(manual) || manual < 0) {
        setError('Ingresa un costo unitario válido para este producto')
        return
      }
      unitCost = manual
      source = 'manual'
    }

    setCart((prev) => [
      ...prev,
      { product_id: selectedProduct.id, product_name: selectedProduct.name, quantity, unit_cost: unitCost, list_price: listPrice, source },
    ])
    setSelectedProductId(null)
    setItemQuantity('1')
    setManualUnitCost('')
    setError('')
  }

  const handleRemoveFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmitOrder = async () => {
    if (!headerForm.supplier_id) {
      setError('Selecciona un proveedor')
      return
    }
    if (!headerForm.order_date) {
      setError('Selecciona la fecha de pedido')
      return
    }
    if (cart.length === 0) {
      setError('Agrega al menos un producto a la orden')
      return
    }
    setSaving(true)
    setError('')
    const payload = {
      supplier_id: Number(headerForm.supplier_id),
      discount_percentage: selectedTier,
      order_date: headerForm.order_date,
      expected_arrival_date: headerForm.expected_arrival_date || undefined,
      notes: headerForm.notes || undefined,
      items: cart.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        ...(item.source === 'manual' ? { unit_cost: item.unit_cost } : {}),
      })),
    }
    try {
      if (editingId) {
        await fxPurchaseOrdersApi.update(editingId, payload)
      } else {
        await fxPurchaseOrdersApi.create(payload)
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || (editingId ? 'Error al actualizar la orden de compra' : 'Error al crear la orden de compra'))
    } finally {
      setSaving(false)
    }
  }

  const openReceive = (order: PurchaseOrder) => {
    setReceivingOrder(order)
    setArrivalDate(todayIsoDate())
    setActionError('')
  }

  const handleConfirmReceive = async () => {
    if (!receivingOrder) return
    setActingId(receivingOrder.id)
    setActionError('')
    try {
      await fxPurchaseOrdersApi.receive(receivingOrder.id, { arrival_date: arrivalDate || undefined })
      setReceivingOrder(null)
      fetchAll()
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Error al marcar la orden como recibida')
    } finally {
      setActingId(null)
    }
  }

  const handleCancel = async (order: PurchaseOrder) => {
    if (!confirm(`¿Cancelar la orden de compra #${order.id} de ${order.supplier_name}? El inventario en tránsito no se registrará.`)) return
    setActingId(order.id)
    setActionError('')
    try {
      await fxPurchaseOrdersApi.cancel(order.id)
      fetchAll()
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Error al cancelar la orden')
    } finally {
      setActingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Órdenes de Compra — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nueva Orden de Compra
        </button>
      </div>

      {actionError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4">{actionError}</div>}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? `Editar Orden de Compra #${editingId}` : 'Nueva Orden de Compra'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Proveedor *</label>
                <select
                  name="supplier_id" value={headerForm.supplier_id} onChange={handleHeaderChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="">Selecciona un proveedor</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {suppliers.length === 0 && (
                  <p className="mt-1 text-xs text-espresso-500">No tienes proveedores de tipo "Producto" activos -- agrega uno primero en la pestaña Proveedores.</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de pedido *</label>
                  <input
                    type="date" name="order_date" value={headerForm.order_date} onChange={handleHeaderChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Llegada estimada</label>
                  <input
                    type="date" name="expected_arrival_date" value={headerForm.expected_arrival_date} onChange={handleHeaderChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                  <p className="text-xs text-espresso-500 mt-1">Normalmente llega en unos 3 días.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Descuento del pedido</label>
                <select
                  name="discount_percentage" value={headerForm.discount_percentage} onChange={handleHeaderChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                >
                  <option value="">Ninguno (costo manual por producto)</option>
                  {DISCOUNT_TIERS.map((t) => (
                    <option key={t} value={t}>{t}%</option>
                  ))}
                </select>
                <p className="text-xs text-espresso-500 mt-1">
                  Aplica a todos los productos del catálogo Fuxion que agregues a esta orden. Los productos fuera del catálogo siempre requieren un costo manual.
                </p>
              </div>

              <div className="bg-cream-100 rounded-lg p-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Producto</label>
                  <select
                    value={selectedProductId ?? ''}
                    onChange={(e) => { setSelectedProductId(e.target.value ? Number(e.target.value) : null); setManualUnitCost('') }}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Selecciona un producto</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Cantidad</label>
                  <input
                    type="number" min="1" value={itemQuantity}
                    onChange={(e) => setItemQuantity(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                {selectedProduct && (
                  usesTierForSelected ? (
                    <div className="w-36">
                      <label className="block text-xs font-medium text-espresso-500 mb-1">Costo unitario</label>
                      <div className="px-3 py-2 text-sm text-espresso-800">S/ {autoUnitCost!.toFixed(2)}</div>
                    </div>
                  ) : (
                    <div className="w-36">
                      <label className="block text-xs font-medium text-espresso-500 mb-1">Costo unitario *</label>
                      <input
                        type="number" step="0.01" min="0" value={manualUnitCost}
                        onChange={(e) => setManualUnitCost(e.target.value)}
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  )
                )}
                <button
                  type="button" onClick={handleAddToCart}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                >
                  Agregar
                </button>
              </div>

              {cart.length > 0 && (
                <div className="border border-espresso-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-espresso-100">
                    <thead className="bg-cream-100">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Producto</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Cant.</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Costo Unit.</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Subtotal</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-espresso-100">
                      {cart.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-espresso-800">{item.product_name}</td>
                          <td className="px-3 py-2 text-sm text-espresso-800">{item.quantity}</td>
                          <td className="px-3 py-2 text-sm text-espresso-800">S/ {item.unit_cost.toFixed(2)}</td>
                          <td className="px-3 py-2 text-sm text-espresso-800">S/ {(item.unit_cost * item.quantity).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => handleRemoveFromCart(index)} className="text-red-600 hover:text-red-700 text-sm">Quitar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 bg-cream-100 text-right text-sm font-semibold text-espresso-800">
                    Total: S/ {cartTotal.toFixed(2)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea
                  name="notes" value={headerForm.notes} onChange={handleHeaderChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="button" onClick={handleSubmitOrder} disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Orden'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {receivingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                Marcar como recibida — Orden #{receivingOrder.id}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de llegada</label>
                <input
                  type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setReceivingOrder(null)} disabled={actingId === receivingOrder.id}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100 disabled:opacity-50">
                  Cancelar
                </button>
                <button type="button" onClick={handleConfirmReceive} disabled={actingId === receivingOrder.id}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {actingId === receivingOrder.id ? 'Guardando...' : 'Confirmar Recepción'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay órdenes de compra registradas todavía.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Fecha Pedido" sortKey="order_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Proveedor" sortKey="supplier_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Descuento" sortKey="discount_percentage" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Fecha Llegada</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Items</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedData.map((o) => (
                  <Fragment key={o.id}>
                    <tr className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {new Date(o.order_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{o.supplier_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {o.discount_percentage ? `${o.discount_percentage}%` : 'Ninguno'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_BADGE_CLASSES[o.status]}`}>
                          {STATUS_LABELS[o.status]}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                        {o.arrival_date ? (
                          new Date(o.arrival_date).toLocaleDateString()
                        ) : o.expected_arrival_date ? (
                          <span className="text-espresso-500">Estimado: {new Date(o.expected_arrival_date).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-espresso-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{o.items.length}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">S/ {orderTotal(o).toFixed(2)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <TableActionButton onClick={() => setExpandedId(expandedId === o.id ? null : o.id)} variant="neutral">
                            {expandedId === o.id ? 'Ocultar detalle' : 'Ver detalle'}
                          </TableActionButton>
                          {o.status === 'en_transito' && (
                            <>
                              <TableActionButton onClick={() => openEdit(o)} variant="primary" disabled={actingId === o.id}>
                                Editar
                              </TableActionButton>
                              <TableActionButton onClick={() => openReceive(o)} variant="success" disabled={actingId === o.id}>
                                Marcar como recibida
                              </TableActionButton>
                              <TableActionButton onClick={() => handleCancel(o)} variant="danger" disabled={actingId === o.id}>
                                Cancelar
                              </TableActionButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === o.id && (
                      <tr className="bg-cream-100">
                        <td colSpan={8} className="px-4 py-4">
                          {o.notes && <p className="text-xs text-espresso-500 mb-2">Notas: {o.notes}</p>}
                          <table className="min-w-full divide-y divide-espresso-200">
                            <thead>
                              <tr>
                                <th className="px-3 py-1 text-left text-xs font-medium text-espresso-500 uppercase">Producto</th>
                                <th className="px-3 py-1 text-left text-xs font-medium text-espresso-500 uppercase">Cantidad</th>
                                <th className="px-3 py-1 text-left text-xs font-medium text-espresso-500 uppercase">Precio Lista</th>
                                <th className="px-3 py-1 text-left text-xs font-medium text-espresso-500 uppercase">Precio Pagado</th>
                                <th className="px-3 py-1 text-left text-xs font-medium text-espresso-500 uppercase">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-espresso-200">
                              {o.items.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-3 py-1 text-sm text-espresso-800">{item.product_name}</td>
                                  <td className="px-3 py-1 text-sm text-espresso-800">{item.quantity}</td>
                                  <td className="px-3 py-1 text-sm text-espresso-800">{item.list_price !== null ? `S/ ${item.list_price.toFixed(2)}` : '-'}</td>
                                  <td className="px-3 py-1 text-sm text-espresso-800">S/ {item.unit_cost.toFixed(2)}</td>
                                  <td className="px-3 py-1 text-sm text-espresso-800">S/ {(item.unit_cost * item.quantity).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
